import { Alert, Button, Col, Row, Spin } from 'antd'
import { useEffect, useState } from 'react'
import { useFormatMessage } from '../../helpers/intl'
import { Charger } from '../../models/charger'
import { Connector } from '../../models/connector'
import { RemoteCommandResponse, RemoteMessageNotConnectedResp } from '../../models/charger-remote'
import { SwtchError } from '../../models/error'
import { RemoteStartCharger, RemoteStopCharger } from '../../services/data-provider/charger-remote'
import { useAppState } from 'state'

interface props {
  charger: Charger
  connector: Connector
}

export const RemoteStartStopController: React.FC<props> = ({ charger, connector }) => {
  const { isMockUpEnabled } = useAppState()
  const [startLoading, setStartLoading] = useState(false)
  const [stopLoading, setStopLoading] = useState(false)
  const [startSuccess, setStartSuccess] = useState(false)
  const [stopSuccess, setStopSuccess] = useState(false)
  const [startAttempted, setStartAttempted] = useState(false)
  const [stopAttempted, setStopAttempted] = useState(false)
  const [startMessage, setStartMessage] = useState<RemoteCommandResponse['message']>()
  const [stopMessage, setStopMessage] = useState<RemoteCommandResponse['message']>()
  const [startConnectionMessage, setStartConnectionMessage] = useState<RemoteMessageNotConnectedResp['error']>('')
  const [stopConnectionMessage, setStopConnectionMessage] = useState<RemoteMessageNotConnectedResp['error']>('')
  const [startError, setStartError] = useState<SwtchError>()
  const [stopError, setStopError] = useState<SwtchError>()

  const remoteStartText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.remoteStartText',
    'Click below to Remotely Start charger S/N (Listing Title). Make sure to check Connector is under "Preparing" status.',
  )

  const remoteStopText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.remoteStopText',
    'Click below to Remotely Stop charger S/N (Listing Title).',
  )

  const remoteStartButtonText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.remoteStartButton',
    'Send RemoteStart to Charger S/N',
  )

  const remoteStopButtonText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.remoteStopButton',
    'Send RemoteStop to Charger S/N',
  )

  const sendingStartRequestText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.sendingStartRequest',
    'Sending RemoteStart request to',
  )

  const sendingStopRequestText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.sendingStopRequest',
    'Sending RemoteStop request to',
  )

  const startSuccessText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.startSuccess',
    'RemoteStart has been sent to 0000 - connector -1 for charger 1111',
  )

  const stopSuccessText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.stopSuccess',
    'RemoteStop has been sent to 0000 - connector -1 for charger 1111',
  )

  const startFailedText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.startFailed',
    'RemoteStart has failed to send to 0000 - connector -1 for charger 1111',
  )

  const stopFailedText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.remoteStartStop.stopFailed',
    'RemoteStop has failed to send to 0000 - connector -1 for charger 1111',
  )

  const acceptedResponseText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.triggerMessage.alert.error.message.accepted',
    'Accepted',
  )

  const rejectedResponseText = useFormatMessage(
    'dashboard.chargerPage.remoteControlTab.triggerMessage.alert.error.message.rejected',
    'Rejected',
  )

  const resetStartView = (loading: boolean) => {
    setStartLoading(loading)
    setStartSuccess(false)
    setStartMessage(undefined)
    setStartConnectionMessage('')
    setStartError(undefined)
  }

  const resetStopView = (loading: boolean) => {
    setStopLoading(loading)
    setStopSuccess(false)
    setStopMessage(undefined)
    setStopConnectionMessage('')
    setStopError(undefined)
  }

  useEffect(() => {
    resetStartView(false)
    resetStopView(false)
    setStartAttempted(false)
    setStopAttempted(false)
  }, [])

  const handleRemoteStart = () => {
    console.log('Starting remote start - isMockUpEnabled:', isMockUpEnabled)
    setStartAttempted(true)
    resetStartView(true)
    RemoteStartCharger(isMockUpEnabled, charger, connector)
      .then((resp) => {
        console.log('RemoteStart response:', resp)
        console.log('Response success field:', resp.success)
        console.log('Response success type:', typeof resp.success)
        console.log('Setting startSuccess to:', resp.success)
        // If success field is missing or undefined, assume success based on message content
        const isSuccess = resp.success !== undefined ? resp.success : (resp.message && typeof resp.message === 'string' && !(resp.message as string).toLowerCase().includes('error'))
        console.log('Final success determination:', isSuccess)
        setStartSuccess(isSuccess)
        if (resp.message && Array.isArray(resp.message)) {
          console.log('Setting start message (array):', resp.message)
          setStartMessage(resp.message)
        } else if (resp.message && typeof resp.message === 'object' && 'error' in resp.message) {
          console.log('Setting start connection message (error):', resp.message.error)
          setStartConnectionMessage(resp.message.error)
        } else if (resp.message && typeof resp.message === 'string') {
          console.log('Setting start message (string):', resp.message)
          // For string messages, we'll create a proper response object
          setStartMessage([{ ocppResponseId: 3, ocppTrace: 'mock-trace-id', status: isSuccess ? 'Accepted' : 'Rejected' }])
        } else {
          console.log('No message handling for response:', resp.message)
        }
      })
      .catch((err: SwtchError) => {
        setStartError(err)
        setStartSuccess(false)
        setStartMessage(undefined)
        setStartConnectionMessage('')
      })
      .finally(() => setStartLoading(false))
  }

  const handleRemoteStop = () => {
    console.log('Starting remote stop - isMockUpEnabled:', isMockUpEnabled)
    setStopAttempted(true)
    resetStopView(true)
    RemoteStopCharger(isMockUpEnabled, charger, connector)
      .then((resp) => {
        console.log('RemoteStop response:', resp)
        console.log('Response success field:', resp.success)
        console.log('Response success type:', typeof resp.success)
        // If success field is missing or undefined, assume success based on message content
        const isSuccess = resp.success !== undefined ? resp.success : (resp.message && typeof resp.message === 'string' && !(resp.message as string).toLowerCase().includes('error'))
        console.log('Final success determination:', isSuccess)
        setStopSuccess(isSuccess)
        if (resp.message && Array.isArray(resp.message)) {
          console.log('Setting stop message (array):', resp.message)
          setStopMessage(resp.message)
        } else if (resp.message && typeof resp.message === 'object' && 'error' in resp.message) {
          console.log('Setting stop connection message (error):', resp.message.error)
          setStopConnectionMessage(resp.message.error)
        } else if (resp.message && typeof resp.message === 'string') {
          console.log('Setting stop message (string):', resp.message)
          // For string messages, we'll create a proper response object
          setStopMessage([{ ocppResponseId: 3, ocppTrace: 'mock-trace-id', status: isSuccess ? 'Accepted' : 'Rejected' }])
        } else {
          console.log('No message handling for response:', resp.message)
        }
      })
      .catch((err: SwtchError) => {
        setStopError(err)
        setStopSuccess(false)
        setStopMessage(undefined)
        setStopConnectionMessage('')
      })
      .finally(() => setStopLoading(false))
  }

  const renderStartMessages = () => {
    console.log('renderStartMessages - startAttempted:', startAttempted, 'startSuccess:', startSuccess, 'startMessage:', startMessage, 'startConnectionMessage:', startConnectionMessage, 'startError:', startError)
    return (
      <>
        {startError && <Alert message={startError.description} type={'error'} />}
        {startAttempted && startSuccess && (
          <Alert
            message={`${startSuccessText
              .replace('0000', `${connector.listing?.title}`)
              .replace('-1', `${connector.connectorId}`)
              .replace('1111', `${charger.chargePointSerialNumber}`)}: ${acceptedResponseText}`}
            description={Array.isArray(startMessage) ? startMessage[0]?.status : 'Command sent successfully'}
            type={'info'}
            style={{ marginBottom: '10px' }}
          />
        )}
        {startAttempted && !startSuccess && (
          <Alert
            message={`${startFailedText
              .replace('0000', `${connector.listing?.title}`)
              .replace('-1', `${connector.connectorId}`)
              .replace('1111', `${charger.chargePointSerialNumber}`)}: ${rejectedResponseText}`}
            description={Array.isArray(startMessage) ? startMessage[0]?.status : 'Command failed'}
            type={'error'}
          />
        )}
        {!startSuccess && startConnectionMessage && <Alert message={startConnectionMessage} type={'error'} />}
      </>
    )
  }

  const renderStopMessages = () => {
    return (
      <>
        {stopError && <Alert message={stopError.description} type={'error'} />}
        {stopAttempted && stopSuccess && (
          <Alert
            message={`${stopSuccessText
              .replace('0000', `${connector.listing?.title}`)
              .replace('-1', `${connector.connectorId}`)
              .replace('1111', `${charger.chargePointSerialNumber}`)}: ${acceptedResponseText}`}
            description={Array.isArray(stopMessage) ? stopMessage[0]?.status : 'Command sent successfully'}
            type={'info'}
            style={{ marginBottom: '10px' }}
          />
        )}
        {stopAttempted && !stopSuccess && (
          <Alert
            message={`${stopFailedText
              .replace('0000', `${connector.listing?.title}`)
              .replace('-1', `${connector.connectorId}`)
              .replace('1111', `${charger.chargePointSerialNumber}`)}: ${rejectedResponseText}`}
            description={Array.isArray(stopMessage) ? stopMessage[0]?.status : 'Command failed'}
            type={'error'}
          />
        )}
        {!stopSuccess && stopConnectionMessage && <Alert message={stopConnectionMessage} type={'error'} />}
      </>
    )
  }

  return (
    <Row>
      <Col span={24}>
        {/* Remote Start Section */}
        <div style={{ marginBottom: '30px' }}>
          <p>
            {remoteStartText.replace('S/N (Listing Title)', charger.chargePointSerialNumber)}
          </p>
          <Button type="primary" onClick={handleRemoteStart} loading={startLoading}>
            {remoteStartButtonText.replace('S/N', charger.chargePointSerialNumber)}
          </Button>
          <Col span={12} style={{ paddingTop: '10px' }}>
            {startLoading && (
              <Alert
                message={
                  <div>
                    <Spin style={{ marginRight: '10px' }} />
                    {sendingStartRequestText} {charger.chargePointSerialNumber}!
                  </div>
                }
              />
            )}
            {!startLoading && renderStartMessages()}
          </Col>
        </div>

        {/* Remote Stop Section */}
        <div>
          <p>
            {remoteStopText.replace('S/N (Listing Title)', charger.chargePointSerialNumber)}
          </p>
          <Button type="primary" onClick={handleRemoteStop} loading={stopLoading}>
            {remoteStopButtonText.replace('S/N', charger.chargePointSerialNumber)}
          </Button>
          <Col span={12} style={{ paddingTop: '10px' }}>
            {stopLoading && (
              <Alert
                message={
                  <div>
                    <Spin style={{ marginRight: '10px' }} />
                    {sendingStopRequestText} {charger.chargePointSerialNumber}!
                  </div>
                }
              />
            )}
            {!stopLoading && renderStopMessages()}
          </Col>
        </div>
      </Col>
    </Row>
  )
}
